import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Package, Shield, Eye, Users, ArrowRight, Send, Truck, MapPin, CheckCircle, Star, Zap, Globe, Heart, Sparkles, Play } from "lucide-react";
import Header from "@/components/layout/Header";
import heroEmotionalBled from "@/assets/hero-emotional-bled-alt.jpg";

// Variation A - Current Hero (baseline)
const HeroVariantA = () => (
  <section className="relative min-h-[70vh] flex items-center pt-6 pb-8 overflow-x-hidden bg-gradient-to-br from-primary/5 via-background to-secondary/5">
    <div className="container mx-auto px-4 relative z-10">
      <div className="grid lg:grid-cols-2 gap-5 lg:gap-10 items-center">
        <div className="lg:text-left">
          <div className="inline-flex items-center bg-muted/60 border border-border/40 rounded-lg px-4 py-2 mb-3">
            <span className="text-sm text-muted-foreground font-medium">🚀 Plateforme en beta test</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-3 leading-[1.1]">
            <span className="block">Transport de colis</span>
            <span className="block text-4xl md:text-5xl lg:text-6xl">
              <span className="text-[#002395]">Fr</span><span className="text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]">an</span><span className="text-[#ED2939]">ce</span>
              <span className="text-foreground"> ⇄ </span>
              <span className="text-[#C1272D]">Mar</span><span className="text-[#006233]">oc</span>
            </span>
            <span className="block">en toute confiance</span>
          </h1>
          
          <p className="text-base md:text-lg text-foreground/70 mb-5 max-w-xl leading-relaxed">
            GoBledPro met en relation particuliers et transporteurs indépendants sélectionnés, pour des envois fiables France ⇄ Maroc.
          </p>
          
          <Link to="/demande-transport">
            <Button size="lg" className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground text-base px-8 h-14 gap-2">
              Trouver un transport
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
        
        <div className="hidden lg:flex items-center justify-center">
          <div className="relative w-[300px] h-[300px] rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
            <Package className="w-24 h-24 text-primary" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Variation B - More visual impact with split layout
const HeroVariantB = () => (
  <section className="relative min-h-[70vh] flex items-center overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-r from-primary to-primary/80" />
    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZoLTJ2LTRoMnY0em0wLTZoLTJ2LTRoMnY0em0wLTZoLTJ2LTRoMnY0em0wLTZoLTJWNmgydjR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
    
    <div className="container mx-auto px-4 relative z-10">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div className="text-white">
          <div className="inline-flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-4">
            <span className="text-sm font-medium">🚀 Plateforme en beta test</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-4 leading-[1.1]">
            Envoyez vos colis<br />
            <span className="text-secondary">France ⇄ Maroc</span><br />
            en toute confiance
          </h1>
          
          <p className="text-lg text-white/80 mb-6 max-w-lg">
            Mise en relation avec des transporteurs indépendants vérifiés. Simple, fiable, transparent.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/demande-transport">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto text-base px-8 h-14 gap-2 font-semibold">
                Envoyer un colis
                <Send className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/annonces-transporteurs">
              <Button size="lg" variant="outline" className="w-full sm:w-auto text-base px-8 h-14 border-white/30 text-white hover:bg-white/10">
                Voir les transporteurs
              </Button>
            </Link>
          </div>
          
          <div className="flex items-center gap-6 mt-6 text-sm text-white/70">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Gratuit</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Sans engagement</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Sécurisé</span>
          </div>
        </div>
        
        <div className="hidden lg:block">
          <div className="relative">
            <div className="absolute -inset-4 bg-white/10 rounded-3xl blur-xl" />
            <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                  <Truck className="w-6 h-6 text-secondary-foreground" />
                </div>
                <div>
                  <p className="font-semibold text-white">+50 transporteurs</p>
                  <p className="text-sm text-white/60">actifs sur la plateforme</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                  <MapPin className="w-5 h-5 text-secondary" />
                  <span className="text-white/80">Paris → Casablanca</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                  <MapPin className="w-5 h-5 text-secondary" />
                  <span className="text-white/80">Lyon → Rabat</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                  <MapPin className="w-5 h-5 text-secondary" />
                  <span className="text-white/80">Marseille → Fès</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Variation C - Minimal, clean, centered
const HeroVariantC = () => (
  <section className="relative min-h-[70vh] flex items-center justify-center text-center overflow-hidden bg-background">
    <div className="absolute inset-0">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full blur-3xl" />
    </div>
    
    <div className="container mx-auto px-4 relative z-10 max-w-4xl">
      <div className="inline-flex items-center bg-primary/10 text-primary rounded-full px-4 py-2 mb-6">
        <span className="text-sm font-medium">🚀 Plateforme en beta test</span>
      </div>
      
      <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground mb-6 leading-tight">
        Le transport de colis<br />
        <span className="bg-gradient-to-r from-[#002395] via-[#ED2939] to-[#006233] bg-clip-text text-transparent">France ⇄ Maroc</span><br />
        simplifié
      </h1>
      
      <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
        Trouvez un transporteur vérifié en quelques clics. GoBledPro connecte la diaspora avec des professionnels de confiance.
      </p>
      
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <Link to="/demande-transport">
          <Button size="lg" className="text-base px-10 h-14 gap-2 shadow-lg shadow-primary/20">
            Demander un transport
            <ArrowRight className="w-5 h-5" />
          </Button>
        </Link>
        <Link to="/devenir-transporteur">
          <Button size="lg" variant="outline" className="text-base px-10 h-14">
            Je suis transporteur
          </Button>
        </Link>
      </div>
      
      <div className="flex items-center justify-center gap-8 mt-10 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          <span>Transporteurs vérifiés</span>
        </div>
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-primary" />
          <span>Tarifs transparents</span>
        </div>
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-primary" />
          <span>Suivi des envois</span>
        </div>
      </div>
    </div>
  </section>
);

// Variation D - Cards focused
const HeroVariantD = () => (
  <section className="relative min-h-[70vh] flex items-center pt-8 pb-12 overflow-hidden bg-gradient-to-b from-muted/50 to-background">
    <div className="container mx-auto px-4 relative z-10">
      <div className="text-center mb-10">
        <div className="inline-flex items-center bg-secondary/20 text-secondary-foreground rounded-lg px-4 py-2 mb-4">
          <span className="text-sm font-medium">🚀 Nouveau : Plateforme en beta test</span>
        </div>
        
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
          Transport colis <span className="text-primary">France ⇄ Maroc</span>
        </h1>
        
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Que souhaitez-vous faire ?
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        <Link to="/demande-transport" className="group">
          <div className="bg-card border border-border rounded-2xl p-8 transition-all duration-300 hover:shadow-xl hover:border-primary/50 hover:-translate-y-1">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
              <Package className="w-7 h-7 text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Envoyer un colis</h2>
            <p className="text-muted-foreground mb-4">
              Trouvez un transporteur de confiance pour acheminer votre colis vers le Maroc ou la France.
            </p>
            <span className="inline-flex items-center text-primary font-medium gap-1 group-hover:gap-2 transition-all">
              Commencer <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>
        
        <Link to="/devenir-transporteur" className="group">
          <div className="bg-card border border-border rounded-2xl p-8 transition-all duration-300 hover:shadow-xl hover:border-secondary/50 hover:-translate-y-1">
            <div className="w-14 h-14 rounded-xl bg-secondary/20 flex items-center justify-center mb-4 group-hover:bg-secondary/30 transition-colors">
              <Truck className="w-7 h-7 text-secondary-foreground" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Devenir transporteur</h2>
            <p className="text-muted-foreground mb-4">
              Rejoignez notre réseau de transporteurs vérifiés et développez votre activité.
            </p>
            <span className="inline-flex items-center text-secondary-foreground font-medium gap-1 group-hover:gap-2 transition-all">
              S'inscrire <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>
      </div>
      
      <div className="flex items-center justify-center gap-6 mt-10 text-sm text-muted-foreground">
        <span>✓ Inscription gratuite</span>
        <span>✓ Sans engagement</span>
        <span>✓ Transporteurs vérifiés</span>
      </div>
    </div>
  </section>
);

// ============== NOUVELLES VARIANTES TRANSFORMÉES ==============

// Variation E - Brutalist / Bold Typography
const HeroVariantE = () => (
  <section className="relative min-h-[80vh] flex items-center overflow-hidden bg-foreground text-background">
    <div className="absolute inset-0 opacity-10">
      <div className="absolute top-0 left-0 w-full h-full" style={{
        backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 50px, hsl(var(--background)) 50px, hsl(var(--background)) 51px)`
      }} />
    </div>
    
    <div className="container mx-auto px-4 relative z-10">
      <div className="max-w-5xl">
        <div className="mb-8">
          <span className="inline-block bg-secondary text-secondary-foreground px-4 py-2 text-sm font-bold uppercase tracking-widest">
            Beta 2025
          </span>
        </div>
        
        <h1 className="text-5xl md:text-7xl lg:text-[120px] font-heading font-black leading-[0.85] tracking-tight mb-8">
          COLIS
          <br />
          <span className="text-secondary">FR→MA</span>
        </h1>
        
        <div className="flex flex-col md:flex-row gap-8 items-start">
          <div className="flex-1">
            <p className="text-xl md:text-2xl text-background/70 font-light leading-relaxed max-w-md">
              La diaspora mérite mieux. Transporteurs vérifiés. Tarifs clairs. Zéro galère.
            </p>
          </div>
          
          <div className="flex flex-col gap-4">
            <Link to="/demande-transport">
              <Button size="lg" variant="secondary" className="text-lg px-10 h-16 font-bold uppercase tracking-wide">
                Envoyer maintenant
              </Button>
            </Link>
            <Link to="/annonces-transporteurs">
              <Button size="lg" variant="outline" className="text-lg px-10 h-16 border-2 border-background/30 text-background hover:bg-background/10 font-medium">
                Voir les trajets
              </Button>
            </Link>
          </div>
        </div>
        
        <div className="mt-16 pt-8 border-t border-background/20">
          <div className="flex flex-wrap gap-12 text-sm uppercase tracking-widest text-background/50">
            <span>+50 transporteurs</span>
            <span>100% vérifiés</span>
            <span>Depuis 2024</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Variation F - Glassmorphism / Futuristic
const HeroVariantF = () => (
  <section className="relative min-h-[80vh] flex items-center overflow-hidden">
    {/* Animated gradient background */}
    <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460]" />
    <div className="absolute inset-0">
      <div className="absolute top-20 left-20 w-72 h-72 bg-[#e94560] rounded-full filter blur-[120px] opacity-30 animate-pulse" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#0f3460] rounded-full filter blur-[150px] opacity-50" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00b4d8] rounded-full filter blur-[200px] opacity-20" />
    </div>
    
    <div className="container mx-auto px-4 relative z-10">
      <div className="max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full px-6 py-3 mb-8">
          <Sparkles className="w-4 h-4 text-[#e94560]" />
          <span className="text-white/80 text-sm">Nouvelle génération de transport</span>
        </div>
        
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-6 leading-tight">
          Le futur du transport
          <br />
          <span className="bg-gradient-to-r from-[#00b4d8] via-[#e94560] to-[#ffd60a] bg-clip-text text-transparent">
            France ⇄ Maroc
          </span>
        </h1>
        
        <p className="text-lg md:text-xl text-white/60 mb-10 max-w-2xl mx-auto">
          Une plateforme intelligente qui connecte vos colis aux meilleurs transporteurs. Rapide, sécurisé, transparent.
        </p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <Link to="/demande-transport">
            <Button size="lg" className="bg-gradient-to-r from-[#e94560] to-[#00b4d8] hover:opacity-90 text-white text-lg px-10 h-16 rounded-xl border-0 shadow-lg shadow-[#e94560]/30">
              Commencer gratuitement
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <Link to="/annonces-transporteurs">
            <Button size="lg" variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 text-lg px-8 h-16 gap-2">
              <Play className="w-5 h-5" />
              Découvrir
            </Button>
          </Link>
        </div>
        
        {/* Glass cards stats */}
        <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
          {[
            { value: '50+', label: 'Transporteurs' },
            { value: '24h', label: 'Réponse moyenne' },
            { value: '100%', label: 'Vérifiés' }
          ].map((stat, i) => (
            <div key={i} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-white/50">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// Variation G - Editorial / Magazine Style
const HeroVariantG = () => (
  <section className="relative min-h-[85vh] flex items-center overflow-hidden bg-[#faf9f6]">
    <div className="container mx-auto px-4 relative z-10">
      <div className="grid lg:grid-cols-12 gap-8 items-center">
        {/* Left content */}
        <div className="lg:col-span-7">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px flex-1 bg-foreground/20 max-w-[60px]" />
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-foreground/60">
              Transport de confiance
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-normal text-foreground leading-[1.1] mb-6">
            Vos colis traversent
            <br />
            la <span className="italic">Méditerranée</span>,
            <br />
            pas vos soucis.
          </h1>
          
          <p className="text-lg text-foreground/60 leading-relaxed max-w-lg mb-8 font-light">
            GoBledPro réunit une communauté de transporteurs vérifiés pour des envois France-Maroc en toute sérénité. Parce que vos proches méritent le meilleur.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/demande-transport">
              <Button size="lg" className="bg-foreground hover:bg-foreground/90 text-background rounded-none text-sm px-8 h-14 font-medium tracking-wide">
                DEMANDER UN TRANSPORT
              </Button>
            </Link>
            <Link to="/devenir-transporteur">
              <Button size="lg" variant="outline" className="rounded-none text-sm px-8 h-14 border-foreground/30 font-medium tracking-wide">
                ESPACE TRANSPORTEUR
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Right visual */}
        <div className="lg:col-span-5 relative">
          <div className="aspect-[4/5] bg-gradient-to-br from-primary/20 to-secondary/20 relative">
            <div className="absolute inset-4 border border-foreground/10" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Globe className="w-20 h-20 text-foreground/20 mx-auto mb-4" />
                <div className="text-6xl font-serif text-foreground/10">FR⇄MA</div>
              </div>
            </div>
            {/* Floating badge */}
            <div className="absolute -left-6 bottom-20 bg-white shadow-xl p-4 border border-foreground/10">
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="w-8 h-8 rounded-full bg-muted border-2 border-white" />
                  ))}
                </div>
                <div>
                  <div className="text-sm font-medium">+50 transporteurs</div>
                  <div className="text-xs text-muted-foreground">actifs ce mois</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Variation H - Playful / Rounded / Friendly
const HeroVariantH = () => (
  <section className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-b from-[#fff5eb] to-[#ffe4cc]">
    {/* Decorative shapes */}
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#ff6b35] rounded-full opacity-10" />
      <div className="absolute top-1/2 -left-40 w-96 h-96 bg-[#004e89] rounded-full opacity-10" />
      <div className="absolute -bottom-20 right-1/3 w-64 h-64 bg-[#1a659e] rounded-full opacity-10" />
    </div>
    
    <div className="container mx-auto px-4 relative z-10">
      <div className="max-w-4xl mx-auto text-center">
        {/* Emoji header */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <span className="text-4xl">🇫🇷</span>
          <div className="flex items-center gap-2 bg-white/80 backdrop-blur rounded-full px-6 py-3 shadow-lg">
            <Heart className="w-5 h-5 text-[#ff6b35]" />
            <span className="font-medium text-foreground">Transport familial</span>
          </div>
          <span className="text-4xl">🇲🇦</span>
        </div>
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-[#1a3a5c] mb-6 leading-tight">
          Envoyez de l'amour
          <br />
          <span className="relative inline-block">
            <span className="relative z-10">au bled</span>
            <span className="absolute bottom-2 left-0 right-0 h-4 bg-[#ff6b35]/30 -z-10 rounded-full" />
          </span>
          , tout simplement
        </h1>
        
        <p className="text-lg md:text-xl text-[#1a3a5c]/70 mb-10 max-w-xl mx-auto">
          Des transporteurs de confiance pour que vos colis arrivent à bon port. Comme à la maison 🏠
        </p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <Link to="/demande-transport">
            <Button size="lg" className="bg-[#ff6b35] hover:bg-[#ff6b35]/90 text-white text-lg px-10 h-16 rounded-full shadow-xl shadow-[#ff6b35]/30 gap-2">
              <Package className="w-5 h-5" />
              Envoyer un colis
            </Button>
          </Link>
          <Link to="/annonces-transporteurs">
            <Button size="lg" variant="outline" className="text-lg px-10 h-16 rounded-full border-2 border-[#1a3a5c]/20 text-[#1a3a5c]">
              Voir les trajets 🚐
            </Button>
          </Link>
        </div>
        
        {/* Trust badges playful */}
        <div className="flex flex-wrap items-center justify-center gap-4">
          {[
            { emoji: '✅', text: 'Vérifiés' },
            { emoji: '💰', text: 'Pas cher' },
            { emoji: '⚡', text: 'Rapide' },
            { emoji: '🔒', text: 'Sécurisé' }
          ].map((badge, i) => (
            <div key={i} className="flex items-center gap-2 bg-white rounded-full px-5 py-3 shadow-md">
              <span className="text-xl">{badge.emoji}</span>
              <span className="font-medium text-[#1a3a5c]">{badge.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// Variation I - Emotional Poster / Western Union Style
const HeroVariantI = () => (
  <section className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-br from-[#1a1a2e] via-[#2d1f3d] to-[#1a1a2e]">
    {/* Subtle pattern overlay */}
    <div className="absolute inset-0 opacity-5" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
    }} />
    
    <div className="container mx-auto px-4 relative z-10">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Left: Text content */}
        <div className="order-2 lg:order-1">
          <div className="inline-flex items-center gap-2 bg-secondary/20 text-secondary rounded-full px-4 py-2 mb-6">
            <Heart className="w-4 h-4" />
            <span className="text-sm font-medium">Plus qu'un colis, un lien</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-[1.1]">
            Vos proches au bled
            <br />
            <span className="text-secondary">n'attendent que vous</span>
          </h1>
          
          <p className="text-lg md:text-xl text-white/70 mb-8 max-w-lg leading-relaxed">
            Chaque colis raconte une histoire. GoBledPro connecte la diaspora avec des transporteurs de confiance pour que vos envois arrivent avec tout l'amour que vous y mettez.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <Link to="/demande-transport">
              <Button size="lg" className="w-full sm:w-auto bg-secondary hover:bg-secondary/90 text-secondary-foreground text-lg px-10 h-16 gap-2 font-semibold shadow-xl shadow-secondary/30">
                Envoyer un colis
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/annonces-transporteurs">
              <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 h-16 border-white/20 text-white hover:bg-white/10">
                Voir les trajets
              </Button>
            </Link>
          </div>
          
          {/* Trust indicators */}
          <div className="flex flex-wrap gap-6 text-sm text-white/60">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-secondary" />
              <span>Transporteurs vérifiés</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-secondary" />
              <span>Envois sécurisés</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-secondary" />
              <span>100% diaspora</span>
            </div>
          </div>
        </div>
        
        {/* Right: Emotional image */}
        <div className="order-1 lg:order-2 relative">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl">
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a2e]/80 via-transparent to-transparent z-10" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a2e]/50 via-transparent to-transparent z-10 lg:hidden" />
            
            <img 
              src={heroEmotionalBled} 
              alt="Famille recevant un colis au Maroc" 
              className="w-full h-[400px] lg:h-[500px] object-cover"
            />
            
            {/* Floating caption */}
            <div className="absolute bottom-6 left-6 right-6 z-20">
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                <p className="text-white text-sm italic">
                  "Le sourire de ma mère quand elle reçoit le colis... ça n'a pas de prix."
                </p>
                <p className="text-white/60 text-xs mt-1">— Karim, Paris</p>
              </div>
            </div>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute -top-4 -right-4 w-24 h-24 bg-secondary/20 rounded-full blur-2xl" />
          <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary/20 rounded-full blur-3xl" />
        </div>
      </div>
    </div>
  </section>
);

const HeroTest = () => {
  const [activeVariant, setActiveVariant] = useState<'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I'>('A');

  const variants = {
    A: { name: 'Actuel (baseline)', component: <HeroVariantA /> },
    B: { name: 'Impact visuel (fond coloré)', component: <HeroVariantB /> },
    C: { name: 'Minimal centré', component: <HeroVariantC /> },
    D: { name: 'Cards / Choix utilisateur', component: <HeroVariantD /> },
    E: { name: '🆕 Brutalist / Bold', component: <HeroVariantE /> },
    F: { name: '🆕 Glassmorphism / Futuriste', component: <HeroVariantF /> },
    G: { name: '🆕 Editorial / Magazine', component: <HeroVariantG /> },
    H: { name: '🆕 Playful / Friendly', component: <HeroVariantH /> },
    I: { name: '🆕 Affiche émotionnelle', component: <HeroVariantI /> },
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Active Variant Display */}
      <main className="pt-16 flex-1">
        {variants[activeVariant].component}
      </main>

      {/* Variant Selector - Bottom Fixed */}
      <div className="sticky bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-sm border-t border-border shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3 overflow-x-auto pb-2">
            <span className="text-sm font-semibold text-foreground whitespace-nowrap">🎨 Variantes :</span>
            {(Object.keys(variants) as Array<keyof typeof variants>).map((key) => (
              <button
                key={key}
                onClick={() => setActiveVariant(key)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeVariant === key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80 text-foreground'
                }`}
              >
                {key}: {variants[key].name}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">
            Page non indexée — visible uniquement via /hero-test
          </p>
        </div>
      </div>
    </div>
  );
};

export default HeroTest;
